import { Component, Input, OnInit } from '@angular/core';
import { RegistrationService } from '../../../services/registration-service';
import MarketWatchListItem, {
  MarketWatchService,
} from 'src/app/services/market-watch-service';
import {
  AccountV2Service,
  AuctionFixture,
  FollowedPlate,
  LatestEvent,
  PlateViewEvent,
  SavedPlate,
} from 'src/app/services/account-v2-service';
import {
  IndexSnapshot,
  STATIC_INDEX_SERIES,
  STATIC_INDEX_SNAPSHOT,
} from 'src/app/services/home-v2-service';

interface IndexChart {
  viewBox: string;
  line: string;
  /** Dashed segment to a year-to-date point ('' when the last point is final). */
  partialLine: string;
  area: string;
  dotX: number;
  dotY: number;
  grid: { y: number; label: string }[];
  xLabels: { x: number; label: string; anchor: string }[];
}

interface StandingRow {
  registration: string;
  /** Canonical DVLA-spaced display form (backend); identity stays registration. */
  formattedRegistration?: string;
  kind: 'Tracking' | 'Saved' | 'Recently viewed';
  statusLine: string;
  contextLine: string;
}

/**
 * Shared Market section (frozen design: "This week in the market" on
 * Homepage A, "Your market" on Homepage B). One component, one data
 * contract; the member standing rows render only when inputs arrive.
 *
 * LIVE: verified/largest recent sales (MarketWatch yearly-top-sold),
 * member standing rows (account followed + recent).
 * STATIC: index tile (platex-index deliverables; see home-v2-service).
 * The auction fixture belongs here later (Phase 4) and is intentionally
 * absent until a truthful fixture state exists.
 */
@Component({
  selector: 'pla-home-market',
  templateUrl: './home-market.component.html',
  styleUrls: ['./home-market.component.scss'],
})
export class HomeMarketComponent implements OnInit {
  @Input() public member = false;
  @Input() public set followed(value: FollowedPlate[] | null) {
    // Every tracked plate, not just the three shown, keeps a tracked plate out
    // of the saved group.
    this.trackedKeys = new Set((value ?? []).map((f) => HomeMarketComponent.key(f.registration)));
    this.followedRows = (value ?? [])
      .slice(0, 3)
      .map((f) => this.eventRow(f.registration, f.formattedRegistration, f.latestEvent, 'Tracking'));
    this.recompose();
  }
  @Input() public set saved(value: SavedPlate[] | null) {
    this.savedRows = (value ?? []).map((s) =>
      this.eventRow(s.registration, s.formattedRegistration, s.latestEvent, 'Saved')
    );
    this.recompose();
  }
  @Input() public set recent(value: PlateViewEvent[] | null) {
    this.recentRows = (value ?? []).map((r) => ({
      registration: r.registration,
      formattedRegistration: r.formattedRegistration,
      kind: 'Recently viewed' as const,
      statusLine: 'Viewed by you',
      contextLine: `Viewed ${new Date(r.viewedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
      })}`,
    }));
    this.recompose();
  }

  public sales: MarketWatchListItem[] | null = null;
  public salesFailed = false;
  public index: IndexSnapshot = STATIC_INDEX_SNAPSHOT;
  /** Real composite series (STATIC), rendered as an inline SVG sparkline. */
  public chart: IndexChart = HomeMarketComponent.buildChart();
  public standingRows: StandingRow[] = [];
  /** Auction calendar strip (LIVE: GET /auction/fixture) — member only. */
  public fixture: AuctionFixture | null = null;
  public followedRows: StandingRow[] = [];
  private savedRows: StandingRow[] = [];
  private trackedKeys = new Set<string>();
  private recentRows: StandingRow[] = [];

  constructor(
    private marketWatchService: MarketWatchService,
    private account: AccountV2Service,
    private registrationService: RegistrationService
  ) {}

  ngOnInit(): void {
    // LIVE: largest verified sales, current year with service-side fallback.
    this.marketWatchService.GetTopSoldThiYear((results) => {
      if (results == null) {
        this.salesFailed = true;
        this.sales = [];
        return;
      }
      // The feed's registration is unspaced (F80FER, 1NR); space it the way
      // the market-watch strip does so 1 NR never reads as INR.
      this.sales = results.slice(0, 4).map((row) => {
        const raw = (row.registration || '').replace(/\s+/g, '');
        return { ...row, formattedRegistration: raw ? this.registrationService.LocalFormatRegistration(raw) : row.formattedRegistration };
      });
    });
    // LIVE: the auction calendar strip belongs to "Your market" (members).
    if (this.member) {
      this.account.getAuctionFixture().subscribe({
        next: (f) => (this.fixture = f),
        error: () => (this.fixture = null),
      });
    }
  }

  /**
   * Deterministic auction-calendar line from the fixture fields — no server
   * prose. e.g. "DVLA May 2026 Timed Online Auction · catalogue published ·
   * 1581 lots · closing today".
   */
  public fixtureLine(): string {
    const f = this.fixture;
    if (!f || !f.hasAuction) return '';
    const bits: string[] = [];
    if (f.title) bits.push(f.title);
    if (f.catalogueAvailable) bits.push('catalogue published');
    if (f.lotCount != null) bits.push(`${f.lotCount} lots`);
    if (f.daysRemaining != null) {
      const d = f.daysRemaining;
      bits.push(d === 0 ? 'closing today' : `${d} day${d === 1 ? '' : 's'} to go`);
    }
    return bits.join(' · ');
  }

  /**
   * Precompute the index sparkline geometry from the real STATIC composite
   * series. Pure and deterministic (no Date/random). viewBox space; the SVG
   * scales to its container. Base 100 line drawn as a reference gridline.
   */
  private static buildChart(): IndexChart {
    const pts = STATIC_INDEX_SERIES;
    const W = 900, H = 250, padL = 6, padR = 46, padT = 16, padB = 28;
    const minV = 80, maxV = 180;
    const n = pts.length;
    const x = (i: number) => padL + (i * (W - padL - padR)) / (n - 1);
    const y = (v: number) =>
      padT + (H - padT - padB) * (1 - (v - minV) / (maxV - minV));

    // Complete years form the solid line; a year-to-date final point is joined
    // by a dashed segment so the line never ends on a provisional figure.
    const lastSolid = pts[n - 1].partial ? n - 2 : n - 1;
    let line = `M${x(0).toFixed(1)},${y(pts[0].value).toFixed(1)}`;
    for (let i = 1; i <= lastSolid; i++)
      line += ` L${x(i).toFixed(1)},${y(pts[i].value).toFixed(1)}`;
    const partialLine =
      lastSolid < n - 1
        ? `M${x(lastSolid).toFixed(1)},${y(pts[lastSolid].value).toFixed(1)} L${x(n - 1).toFixed(1)},${y(pts[n - 1].value).toFixed(1)}`
        : '';
    const area =
      line +
      ` L${x(lastSolid).toFixed(1)},${H - padB} L${x(0).toFixed(1)},${H - padB} Z`;

    const grid = [100, 140, 180].map((v) => ({
      y: y(v),
      label: v.toString(),
    }));

    // Every other year counted back from the last point, so the final label
    // ("2026 so far") never collides with its neighbour.
    const xLabels = pts
      .map((p, i) => ({ p, i }))
      .filter(({ i }) => (n - 1 - i) % 2 === 0)
      .map(({ p, i }) => ({
        x: x(i),
        label: p.partial ? `${p.year} so far` : p.year.toString(),
        anchor: i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle',
      }));

    return {
      viewBox: `0 0 ${W} ${H}`,
      line,
      partialLine,
      area,
      dotX: x(n - 1),
      dotY: y(pts[n - 1].value),
      grid,
      xLabels,
    };
  }

  // A tracked or saved plate's row says what last changed on it (the market
  // events feed), not just that it is tracked or saved.
  private eventRow(
    registration: string,
    formattedRegistration: string | undefined,
    e: LatestEvent | null,
    kind: 'Tracking' | 'Saved'
  ): StandingRow {
    let statusLine: string = kind;
    let contextLine = 'No recent movement';
    if (e) {
      const price =
        e.pricePence != null
          ? `£${Math.round(e.pricePence / 100).toLocaleString('en-GB')}`
          : null;
      switch (e.eventType) {
        case 'auction_result':
          statusLine = price ? `Realised ${price}` : 'Auction result recorded';
          contextLine = 'Watching for a resale listing';
          break;
        case 'verified_sale':
          statusLine = price ? `Sale verified · ${price}` : 'Sale verified';
          contextLine = 'New comparable sale on record';
          break;
        case 'price_change':
          statusLine = price ? `Asking ${price}` : 'Price changed';
          contextLine = 'Price moved at a seller';
          break;
        case 'listing_new':
        case 'becomes_available':
          statusLine = price ? `For sale · ${price}` : 'For sale';
          contextLine = 'Now on the market';
          break;
        case 'no_longer_available':
          statusLine = 'No longer available';
          contextLine = 'Left the market';
          break;
        default:
          statusLine = kind;
          contextLine = 'Latest change on its record';
      }
    }
    return { registration, formattedRegistration, kind, statusLine, contextLine };
  }

  // Tracked plates, saved plates and recently viewed are three labelled
  // groups. A plate appears once, in the first group it belongs to: a saved
  // plate that is also tracked shows under tracked, and nothing shown above
  // is repeated under Recently viewed.
  public savedOnlyRows: StandingRow[] = [];
  public recentOnlyRows: StandingRow[] = [];

  private static key(r: string): string {
    return (r || '').replace(/\s+/g, '').toUpperCase();
  }

  private recompose(): void {
    const key = HomeMarketComponent.key;
    const shown = new Set(this.followedRows.map((r) => key(r.registration)));
    this.savedOnlyRows = this.savedRows.filter((r) => !this.trackedKeys.has(key(r.registration))).slice(0, 3);
    this.savedOnlyRows.forEach((r) => shown.add(key(r.registration)));
    this.recentOnlyRows = this.recentRows.filter((r) => !shown.has(key(r.registration))).slice(0, 2);
    this.standingRows = [...this.followedRows, ...this.savedOnlyRows, ...this.recentOnlyRows];
  }

  public plateUrl(registration: string): string {
    return `/plates/${(registration || '').replace(/\s/g, '')}`;
  }

  public formatPrice(item: MarketWatchListItem): string {
    // sold_price is PENCE (same as landing-market-watch: sold_price / 100).
    const pence = Number(String(item.sold_price).replace(/[^0-9.]/g, ''));
    if (!isFinite(pence) || pence <= 0) return String(item.sold_price ?? '');
    return `£${Math.round(pence / 100).toLocaleString('en-GB')}`;
  }

  public formatDate(item: MarketWatchListItem): string {
    const d = new Date(item.sold_date);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
}
